"use client";
import React, { useState, useMemo, useEffect, useRef, ChangeEvent } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import CardList from "../Card/CardList";
import AppliedFilters from "../Filters/AppliedFilters";
import ResponsiveFilters from "../Filters/DateFilter";
import PaginationControls from "../Pagination/PaginationControls";
import { Publication, PublicationsData } from "@/services/publications/types";
import { GalleryGrid } from "../GalleryGrid";
import { useTranslations } from "next-intl";
import { InputSearchBar, InputSearchBarElement } from "@ama-pt/agora-design-system";

export default function PublicationsList({
  items = [],
  type,
}: PublicationsData) {
  const t = useTranslations('PublicationCards');
  const translations = useTranslations();

  const ITEMS_PER_PAGE = 12;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchRef = useRef<InputSearchBarElement>(null);

  const getPageFromUrl = () => {
    const page = searchParams?.get("page");
    const parsed = page ? parseInt(page, 10) : 1;
    return Math.max(1, isNaN(parsed) ? 1 : parsed);
  };

  const startDate = searchParams?.get("startDate") || undefined;
  const endDate = searchParams?.get("endDate") || undefined;
  const publicationType = searchParams?.get("publicationType") || undefined;
  const contentTheme = searchParams?.get("contentTheme") || undefined;
  const region = searchParams?.get("region") || undefined;
  const searchTerm = searchParams?.get("search") || "";

  const [searchValue, setSearchValue] = useState(searchTerm);
  const [isListening, setIsListening] = useState(false);
  const [publicationTypes, setPublicationTypes] = useState<{value: string, label: string}[]>([]);
  const [contentThemes, setContentThemes] = useState<{value: string, label: string}[]>([]);
  const [regions, setRegions] = useState<{value: string, label: string}[]>([]);

  // Keep the last non-empty items so that if the prop temporarily becomes [] (e.g. during
  // reconciliation) we never flash an empty state — the previously loaded data stays visible.
  const lastItemsRef = useRef<Publication[]>(items);
  if (items && items.length > 0) {
    lastItemsRef.current = items;
  }

  // Extract filter option lists from items
  useEffect(() => {
    if (items?.length > 0) {
      const uniqueTypes = new Set<string>();
      const uniqueContentThemes = new Set<string>();
      const uniqueRegions = new Set<string>();

      items.forEach(item => {
        if (item.publicationType) uniqueTypes.add(item.publicationType);
        if (item.contentTheme) uniqueContentThemes.add(item.contentTheme);
        if (item.region) uniqueRegions.add(item.region);
      });

      setPublicationTypes(
        Array.from(uniqueTypes).sort().map(t => ({
          value: t.toLowerCase().replace(/\s+/g, '_'),
          label: t,
        }))
      );
      setContentThemes(
        Array.from(uniqueContentThemes).sort().map(t => ({
          value: t.toLowerCase().replace(/\s+/g, '_'),
          label: t,
        }))
      );
      setRegions(
        Array.from(uniqueRegions).sort().map(r => ({
          value: r.toLowerCase().replace(/\s+/g, '_'),
          label: r,
        }))
      );
    }
  }, [items]);

  useEffect(() => {
    setSearchValue(searchTerm);
  }, [searchTerm]);

  // Filtering is a pure derivation — useMemo keeps it synchronous and eliminates all
  // race conditions that useEffect+setState introduced (loading flash, empty-state flash).
  const filteredData = useMemo(() => {
    const sourceItems = lastItemsRef.current;
    if (!sourceItems?.length) return [];

    let filtered = [...sourceItems];

    if (startDate) {
      const startDateTime = new Date(startDate).getTime();
      filtered = filtered.filter(item => {
        if (!item.publishedAt) return true;
        return new Date(item.publishedAt).getTime() >= startDateTime;
      });
    }

    if (endDate) {
      const endDateTime = new Date(endDate).getTime();
      filtered = filtered.filter(item => {
        if (!item.publishedAt) return true;
        return new Date(item.publishedAt).getTime() <= endDateTime;
      });
    }

    if (publicationType) {
      const typeName = publicationType.replace(/_/g, " ");
      filtered = filtered.filter(item =>
        item.publicationType &&
        item.publicationType.toLowerCase() === typeName.toLowerCase()
      );
    }

    if (contentTheme) {
      const contentThemeName = contentTheme.replace(/_/g, " ");
      filtered = filtered.filter(item =>
        item.contentTheme &&
        item.contentTheme.toLowerCase() === contentThemeName.toLowerCase()
      );
    }

    if (region) {
      const regionName = region.replace(/_/g, " ");
      filtered = filtered.filter(item =>
        item.region &&
        item.region.toLowerCase() === regionName.toLowerCase()
      );
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.publicationType?.toLowerCase().includes(searchLower) ||
        item.contentTheme?.toLowerCase().includes(searchLower) ||
        item.region?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  // items is intentionally in deps to re-run memo when the prop changes,
  // but we read from lastItemsRef so an empty items never clears the results.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, startDate, endDate, publicationType, contentTheme, region, searchTerm]);

  const currentPage = getPageFromUrl();
  const paginatedItems = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const hasFilters = Boolean(startDate || endDate || publicationType || contentTheme || region || searchTerm);

  const onSearchActivateHandler = (term: string) => {
    const params = new URLSearchParams(searchParams?.toString());
    if (term.trim()) {
      params.set("search", term.trim());
    } else {
      params.delete("search");
    }
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const onSearchChangeHandler = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(evt.target.value);
  };

  const onVoiceActivateHandler = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Seu navegador não suporta reconhecimento de voz. Tente usar Chrome, Edge ou Safari.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'pt-PT';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchValue(transcript);
      setTimeout(() => onSearchActivateHandler(transcript), 100);
    };

    recognition.onend = () => setIsListening(false);

    recognition.onerror = (event: any) => {
      console.error('Erro no reconhecimento de voz:', event.error);
      setIsListening(false);
      switch (event.error) {
        case 'network':
          alert('Erro de rede. Verifique sua conexão com a internet.');
          break;
        case 'not-allowed':
          alert('Permissão de microfone negada. Ative o microfone nas configurações do navegador.');
          break;
        case 'no-speech':
          alert('Nenhuma fala detectada. Tente novamente.');
          break;
        case 'audio-capture':
          alert('Microfone não encontrado. Verifique se está conectado.');
          break;
        default:
          alert(`Erro no reconhecimento de voz: ${event.error}`);
      }
    };

    try {
      recognition.start();
    } catch (error) {
      console.error('Erro ao iniciar reconhecimento:', error);
      setIsListening(false);
      alert('Erro ao iniciar reconhecimento de voz. Tente novamente.');
    }
  };

  const getPublicationTypeLabel = () => {
    if (!publicationType) return undefined;
    return publicationTypes.find(t => t.value === publicationType)?.label;
  };

  const getContentThemeLabel = () => {
    if (!contentTheme) return undefined;
    return contentThemes.find(t => t.value === contentTheme)?.label;
  };

  const getRegionLabel = () => {
    if (!region) return undefined;
    return regions.find(r => r.value === region)?.label;
  };

  return (
    <div className="flex flex-col xl:flex-row gap-[64px] xl:gap-[136px]">
      <div className="w-full xl:w-3/12">
        <ResponsiveFilters
          activeDate
          activePubAndRegion={type !== "image" && type !== "pressRelease"}
          activeImage={type === "image"}
          publicationTypeData={publicationTypes}
          contentThemeData={contentThemes}
          regionData={regions}
          selectedTheme={contentTheme}
          selectedPublicationType={publicationType}
          selectedRegion={region}
        />
        <AppliedFilters
          startDate={startDate}
          endDate={endDate}
          publicationType={publicationType}
          contentTheme={contentTheme}
          contentThemeLabel={getContentThemeLabel()}
          publicationTypeLabel={getPublicationTypeLabel()}
          region={region}
          regionLabel={getRegionLabel()}
          searchTerm={searchTerm}
        />
      </div>

      <div className="w-full xl:w-9/12">
        <div className="my-32">
          <InputSearchBar
            ref={searchRef}
            id={`publications-${type}-search-input`}
            className="border-0 text-primary-500"
            placeholder={translations("SearchPanel.placeholderSearch")}
            label={type === "image" ? translations("SearchPanel.labelImage") : type === "video" ? translations("SearchPanel.labelVideo") : translations("SearchPanel.labelComunicados")}
            searchActionAltText={translations("SearchPanel.search")}
            voiceActionAltText={isListening ? "Escutando..." : translations("SearchPanel.searchWithVoice")}
            value={searchValue}
            onChange={onSearchChangeHandler}
            onSearchActivate={onSearchActivateHandler}
            onVoiceActivate={onVoiceActivateHandler}
          />

          {isListening && (
            <div className="mt-16 flex items-center justify-center text-blue-600">
              <div className="animate-pulse flex items-center">
                <svg className="w-20 h-20 mr-8" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7 4a3 3 0 616 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 715 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{translations("SearchPanel.labelVoice")}</span>
              </div>
            </div>
          )}
        </div>

        {paginatedItems.length > 0 ? (
          <>
            {type !== "image" ?
              <CardList type={"publicacoes"} items={paginatedItems} /> :
              <GalleryGrid items={paginatedItems} />
            }

            {totalPages > 1 && (
              <div className="mt-32 flex justify-center">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                />
              </div>
            )}
          </>
        ) : (
          <div className="w-full py-64 text-center">
            <p className="text-lg text-gray-600">
              {type === "image" ? t("nones") : t("none")} {type === "image" ? translations("SearchPanel.image") : type === "video" ? translations("SearchPanel.video") : translations("SearchPanel.comunicadoImprensa")} {t("found")}{" "}
              {hasFilters ? t("filters") : ""}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
